import React, {useEffect, useMemo} from "react";
import {Dashboard} from "@uppy/react";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import {useSelector} from "react-redux";
import Uppy from "@uppy/core";
import AwsS3Multipart from "@uppy/aws-s3-multipart";
import {PluginUpload} from "../../../widgets/uppy/PluginUpload";
import Box from "@uppy/box";
import Dropbox from "@uppy/dropbox";
import OneDrive from "@uppy/onedrive";
import GoogleDrive from "@uppy/google-drive";
import Url from "@uppy/url";
import Swal from "sweetalert2";
import {useTranslation} from "react-i18next";
import {loadParameter} from "../../../redux_slices/contextSlice";

const UploadDashboard = props => {
    const {t} = useTranslation();

    const {selectedTrack, setDisableSelector} = props;
    const currentToken = ""; //TODO remove-me;

    const {REACT_APP_BACKEND_API_URL, REACT_APP_APP_ENV} = process.env;

    const allowedLanguages = useSelector(state => loadParameter(state, "allowedLanguages"));

    const maxFileSize = 5000000000;
    const maxNumberOfFiles = 20;

    const uppy = useMemo(() => {
        return Uppy({
            id: "upload_video",
            meta: {uploadType: "videoUpload"},
            debug: REACT_APP_APP_ENV !== "prod",
            restrictions: {
                maxFileSize,
                maxNumberOfFiles,
                minNumberOfFiles: 1,
                allowedFileTypes: [".mp4", ".ogg", ".mov", ".webm", ".wmv", ".avi", "application/pdf"],
            },
            trigger: ".UppyModalOpenerBtn",
            inline: true,
            showLinkToFileUploadResult: false,
            showProgressDetails: true,
            note: "Video and PDF files only",
            height: 400,
            autoProceed: false,
            browserBackButtonClose: false,
            target: ".DashboardContainer",
            replaceTargetContent: true,
            hideProgressAfterFinish: false,
            hideUploadButton: false,
            hideRetryButton: true,
            allowMultipleUploads: false,
            onBeforeFileAdded: (currentFile, files) => {
                if (currentFile && (currentFile.type.includes("video") || currentFile.type.includes("pdf"))) {
                    if (currentFile.data.size > maxFileSize) {
                        //set our limit here
                        uppy.info(`File "${currentFile.name}" is too big`, "error", 4000);
                        return false;
                    }

                    if (!currentFile.name) {
                        uppy.info(`File should have a name`, "error", 4000);
                        return false;
                    }

                    if (Object.keys(files).length === maxNumberOfFiles) {
                        uppy.info(`Maximum ${maxNumberOfFiles} file are allowed`, "error", 4000);
                        return false;
                    }

                    return currentFile;
                }
            },
            onBeforeUpload: files => {
                let filesWithoutName = [];
                let filesWithoutLanguage = [];
                let filesWithInvalidLanguage = [];
                let filesWithMetadata = {};
                Object.keys(files).forEach(fileID => {
                    const file = files[fileID];
                    if (file.meta.language?.length) {
                        try {
                            let language = file.meta.language;
                            let parts = language.split("-");
                            if (parts?.length === 2) {
                                parts[0] = parts[0].toLowerCase();
                                parts[1] = parts[1].toUpperCase();
                                language = `${parts[0]}-${parts[1]}`;
                                file.meta.language = language;
                            }
                        } catch (err) {
                            console.error(err);
                        }
                    }
                    if (!file.meta.name) filesWithoutName.push(file.name);
                    else if (!file.meta.language) filesWithoutLanguage.push(file.name);
                    else if (!allowedLanguages || allowedLanguages?.indexOf(file.meta.language) === -1)
                        filesWithInvalidLanguage.push(file.name);
                    else {
                        const metadata = {
                            ...file.meta,
                            title: file.meta.name,
                        };

                        filesWithMetadata[`${fileID}`] = {
                            ...file,
                            meta: metadata,
                        };
                    }
                });

                if (filesWithoutName && filesWithoutName.length) {
                    filesWithoutName.forEach(fileName =>
                        uppy.info(`File  '${fileName}'  needs to have a NAME`, "error", 4000)
                    );
                    // uppy.info(`All files need to have a TITLE`, "error", 4000);
                    return false;
                }

                if (filesWithoutLanguage && filesWithoutLanguage.length) {
                    filesWithoutLanguage.forEach(fileName =>
                        uppy.info(`LANGUAGE must be specified for '${fileName}'`, "error", 4000)
                    );
                    // uppy.info(`All files need to have a TITLE`, "error", 4000);
                    return false;
                }

                if (filesWithInvalidLanguage && filesWithInvalidLanguage.length) {
                    filesWithInvalidLanguage.forEach(fileName =>
                        uppy.info(
                            `Invalid LANGUAGE for '${fileName}'. Valid languages are: ${allowedLanguages}`,
                            "error",
                            4000
                        )
                    );
                    // uppy.info(`All files need to have a TITLE`, "error", 4000);
                    return false;
                }

                return filesWithMetadata;
            },
        })
            .use(AwsS3Multipart, {
                companionUrl: `${REACT_APP_BACKEND_API_URL}`,
                companionHeaders: {
                    "Access-Control-Allow-Origin": "*",
                    "uppy-auth-token": currentToken,
                },
            })
            .use(PluginUpload, {
                companionUrl: `${REACT_APP_BACKEND_API_URL}`,
                companionHeaders: {
                    "uppy-auth-token": currentToken,
                },
            })
            .use(Box, {
                companionUrl: `${REACT_APP_BACKEND_API_URL}`,
                companionHeaders: {
                    "uppy-auth-token": currentToken,
                },
            })
            .use(Dropbox, {
                companionUrl: `${REACT_APP_BACKEND_API_URL}`,
                companionHeaders: {
                    "uppy-auth-token": currentToken,
                },
            })
            .use(OneDrive, {
                companionUrl: `${REACT_APP_BACKEND_API_URL}`,
                companionHeaders: {
                    "uppy-auth-token": currentToken,
                },
            })
            .use(GoogleDrive, {
                companionUrl: `${REACT_APP_BACKEND_API_URL}`,
                companionHeaders: {
                    "uppy-auth-token": currentToken,
                },
            })
            .use(Url, {
                companionUrl: `${REACT_APP_BACKEND_API_URL}`,
                companionHeaders: {
                    "uppy-auth-token": currentToken,
                },
            })
            .on("upload-error", (file, error, response) => {
                uppy.info(error.stack, "error", 4000);
            })
            .on("error", error => {
                uppy.info(error.stack, "error", 4000);
            })
            .on("cancel-all", res => {
                setDisableSelector(false);
            })
            .on("upload-error", (file, error) => {
                uppy.info(t("ADMINPANEL.UPLOAD.DASHBOARD.ERR.FILE", {id: file.id}), "error", 4000);
                uppy.info(t("ADMINPANEL.UPLOAD.DASHBOARD.ERR", {error}), "error", 4000);
            })
            .on("complete", result => {
                Swal.fire({text: t("ADMINPANEL.UPLOAD.DASHBOARD.UPLOADED")});
                setDisableSelector(false);
            })
            .on("upload", async data => {
                if (!selectedTrack || !selectedTrack.track_id)
                    throw Error(t("ADMINPANEL.UPLOAD.DASHBOARD.ERR.NO_TRACK"));
                setDisableSelector(true);

                await data.fileIDs.map(fileID =>
                    uppy.setFileMeta(fileID, {
                        track_id: `${selectedTrack.track_id}`,
                    })
                );
            });
    }, [selectedTrack]);

    useEffect(() => {
        return () => uppy.close();
    }, []);

    return uppy ? (
        <Dashboard
            uppy={uppy}
            width="100%"
            plugins={["Box", "Dropbox", "OneDrive", "GoogleDrive", "Url"]}
            metaFields={[
                {id: "name", name: "Name", placeholder: "Video title or document name"},
                {id: "language", name: "Language", placeholder: `Available languages are: ${allowedLanguages}.`},
            ]}
            proudlyDisplayPoweredByUppy={false}
        />
    ) : (
        <div className={"alert alert-warning"}>{t("ADMINPANEL.UPLOAD.DASHBOARD.LOADING")}</div>
    );
};

export default UploadDashboard;
